import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Flex, HStack, SimpleGrid, Skeleton, Stack, useBreakpointValue, } from '@chakra-ui/react';
import { GridPagination } from '@client/components/collections/GridPagination';
import { STANDARD_GRID_COLUMNS, STANDARD_GRID_GAP, } from '@client/constants';
import { animationName, scrollTargetOffset } from '@client/theme';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';
const NUM_SKELETON_ROWS = 2;
const SPACING_UNIT_PX = 4;
const MOBILE_CARD_WIDTH = 275;
const MOBILE_OVERSCAN = 3;
const GRID_GAP_PX = STANDARD_GRID_GAP * SPACING_UNIT_PX;
export const ItemGrid = (props) => {
    const { items, renderItem, getItemKey, isLoading, numPlaceholders, columns = STANDARD_GRID_COLUMNS, skeletonAspectRatio = 1, } = props;
    const maxNumColumns = useBreakpointValue(columns) ?? 1;
    const numColumns = maxNumColumns;
    const numSkeletons = numPlaceholders ?? numColumns * NUM_SKELETON_ROWS;
    const minItemWidth = 275;
    const maxItemWidth = props.maxItemWidth ?? 400;
    const isMobile = maxNumColumns === 1;
    const scrollRef = useRef(null);
    const virtualizer = useVirtualizer({
        count: items.length,
        horizontal: true,
        enabled: isMobile,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => MOBILE_CARD_WIDTH,
        gap: GRID_GAP_PX,
        overscan: MOBILE_OVERSCAN,
        initialRect: { width: window.innerWidth, height: 0 },
    });
    const [page, setPage] = useState(1);
    const gridRef = useRef(null);
    useEffect(() => {
        virtualizer.scrollToOffset(0);
        setPage(1);
    }, [items]);
    const onPageChange = (nextPage) => {
        setPage(nextPage);
        gridRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };
    const pagedItems = props.pageSize
        ? items.slice((page - 1) * props.pageSize, page * props.pageSize)
        : items;
    const showPagination = !!props.pageSize &&
        !isLoading &&
        items.length > props.pageSize;
    const animatedItem = (child, key) => (_jsx(Box, { minWidth: minItemWidth, maxWidth: maxItemWidth, animation: `${animationName.itemGridEnter} 0.25s ease-out`, children: child }, key));
    if (isMobile) {
        const visibleItems = isLoading
            ? []
            : virtualizer.getVirtualItems();
        const lastVisibleItem = visibleItems[visibleItems.length - 1];
        const paddingStart = visibleItems[0]?.start ?? 0;
        const paddingEnd = lastVisibleItem
            ? virtualizer.getTotalSize() - lastVisibleItem.end
            : 0;
        return (_jsx(Box, { ref: scrollRef, overflowX: "auto", p: 5, m: -5, scrollbarWidth: "none", children: _jsxs(HStack, { alignItems: "start", width: "max-content", gap: STANDARD_GRID_GAP, pl: `${paddingStart}px`, pr: `${paddingEnd}px`, children: [isLoading && (_jsxs(_Fragment, { children: [_jsx(Skeleton, { width: minItemWidth, aspectRatio: skeletonAspectRatio }), _jsx(Skeleton, { width: minItemWidth, aspectRatio: skeletonAspectRatio })] })), visibleItems.map((virtualItem) => (_jsx(Box, { width: MOBILE_CARD_WIDTH, flexShrink: 0, children: renderItem(items[virtualItem.index], true) }, getItemKey(items[virtualItem.index], virtualItem.index))))] }) }));
    }
    return (_jsxs(Stack, { ref: gridRef, width: "100%", scrollMarginTop: scrollTargetOffset, gap: 5, children: [_jsxs(SimpleGrid, { gap: STANDARD_GRID_GAP, columns: numColumns, alignItems: "start", width: "100%", children: [isLoading &&
                        Array.from({ length: numSkeletons }).map((_, i) => (_jsx(Skeleton, { width: "100%", aspectRatio: skeletonAspectRatio }, i))), !isLoading &&
                        pagedItems.map((item, index) => animatedItem(renderItem(item, false), getItemKey(item, index)))] }), showPagination && props.pageSize && (_jsx(Flex, { justifyContent: "center", pt: STANDARD_GRID_GAP, children: _jsx(GridPagination, { count: items.length, pageSize: props.pageSize, page: page, onPageChange: onPageChange }) }))] }));
};
